import Button from "../../components/Button/Button";
import "./RankingPage.css";
import { useState, useCallback, useEffect } from "react";
import Dropdown from "../../components/Dropdown/Dropdown";
import { GetRankings } from "../../services/rankings.service";
import { Link } from "react-router-dom";
import {
  CategoriesResponse,
  IRankings,
  IRankingsRequest,
  SubcategoriesResponse,
} from "../../types/types";
import { handlePrice } from "../../utils/formatdate";
import {
  FetchCategorieslookup,
  FetchSubcategorieslookup,
} from "../../services/lookup.service";
import Tooltip from "../../components/ToolTip/ToolTip";
import Table from "../../components/Table/Table";

const RankingPage = () => {
  const [state, setState] = useState({
    selectedSubcategories: [] as string[], // Holds selected subcategories
    selectedCategory: "",
    isOpen: false, // Handles if the dropdown is open
    isFilterOpen: false,
    isLoading: false,

    rankings: [] as IRankings[],
    categories: [] as CategoriesResponse[],
    subcategories: [] as SubcategoriesResponse[],
  });

  const [filter, setFilter] = useState({
    main: "all",
    sub: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        toggleState("isLoading", true);
        const res = await GetRankings({});
        const CategoryResponse = await FetchCategorieslookup();
        if (res.error && CategoryResponse.error) {
          setState((prev) => ({ ...prev, rankings: [], categories: [] }));
        }
        setState((pre) => ({
          ...pre,
          isLoading: false,
          rankings: res.data,
          categories: Array.isArray(CategoryResponse.data)
            ? CategoryResponse.data
            : [],
        }));
      } catch (err) {
        console.error(err);
      } finally {
        toggleState("isLoading", false);
      }
    };
    fetchData();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const SubcategoryResponse =
        filter.main === "all"
          ? await FetchSubcategorieslookup("")
          : await FetchSubcategorieslookup(state.selectedCategory);
      if (SubcategoryResponse.error) {
        setState((pre) => ({ ...pre, isLoading: false, subcategories: [] }));
        return;
      }
      setState((pre) => ({
        ...pre,
        subcategories: Array.isArray(SubcategoryResponse.data)
          ? SubcategoryResponse.data
          : [],
      }));
    };
    fetchData();
  }, [state.selectedCategory, filter.main]);

  const toggleState = (key: keyof typeof state, value: boolean) => {
    setState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const handleToggle = () => {
    setState((pre) => ({ ...pre, isFilterOpen: !pre.isFilterOpen }));
  };

  const handleMainFilter = async (filter: string, value?: string) => {
    setFilter(() => ({ main: filter, sub: "" }));
    setState((pre) => ({ ...pre, isLoading: true }));
    const request: IRankingsRequest = {
      category_id: value as string,
      subcategory_ids: [],
    };
    const response = await GetRankings(request);
    if (response.error) {
      console.error(response.error);
      return;
    }
    setState((pre) => ({
      ...pre,
      isLoading: false,
      selectedCategory: value ? value : "",
      selectedSubcategories: [],
      rankings: response.data,
    }));
  };

  const handleSubFilter = (filter: string) => {
    setFilter((pre) => ({ ...pre, sub: filter }));
  };

  const handleSubcategorySelection = useCallback(
    async (subcategory: string) => {
      const subcategories = [...state.selectedSubcategories, subcategory];
      const request: IRankingsRequest = {
        subcategory_ids: subcategories,
      };
      setState((pre) => ({ ...pre, isLoading: true }));
      const response =
        filter.main !== "all"
          ? await GetRankings({
              ...request,
              category_id: state.selectedCategory,
            })
          : await GetRankings(request);
      setState((prevState) => {
        const updatedSubcategories = prevState.selectedSubcategories.includes(
          subcategory
        )
          ? prevState.selectedSubcategories.filter(
              (item) => item !== subcategory
            )
          : [...prevState.selectedSubcategories, subcategory];

        return {
          ...prevState,
          isLoading: false,
          selectedSubcategories: updatedSubcategories,
          rankings: response.data,
        };
      });
      state.selectedSubcategories.length >= 0 && handleSubFilter("subcategory");
    },
    [filter.main, state.selectedCategory, state.selectedSubcategories]
  );

  const handleRemoveSubcategory = useCallback(
    async (subcategory: string) => {
      const subcategories = state.selectedSubcategories.filter(
        (value) => value !== subcategory
      );
      const request: IRankingsRequest = {
        subcategory_ids: subcategories,
      };
      setState((pre) => ({ ...pre, isLoading: true }));
      const response =
        filter.main !== "all"
          ? await GetRankings({
              ...request,
              category_id: state.selectedCategory,
            })
          : await GetRankings(request);
      setState((prevState) => ({
        ...prevState,
        isLoading: false,
        selectedSubcategories: prevState.selectedSubcategories.filter(
          (item) => item !== subcategory
        ),
        rankings: response.data,
      }));
      state.selectedSubcategories.length === 1 && handleSubFilter("");
    },
    [filter.main, state.selectedCategory, state.selectedSubcategories]
  );

  const columns = [
    {
      key: "rank",
      header: (
        <>
          <h6>Rank</h6>
        </>
      ),
      className: "text-center",
      render: (value: any) => <h4 className="m-0 p-0 me-3">{value}</h4>,
    },
    {
      key: "entity",
      header: "",
      render: (value: any, row: any) => (
        <div className="d-flex align-items-center">
          {row.image_url ? (
            <img src={row.image_url} className="image" alt={row.entity_name} />
          ) : (
            <div
              style={{ borderRadius: "4px 4px 0px 0px" }}
              className={`default-user-img image text-dark-purple bg-extra-light-purple d-flex justify-content-center align-items-center chat-image`}
            >
              <i className="fa-solid fa-user" style={{ fontSize: 20 }}></i>
            </div>
          )}
          <Link
            className="text-decoration-none purple-color ms-3 line-18"
            to={`/${row.entity_public_id}`}
            state={{ id: row.entity_id }}
          >
            {row.entity_name}
          </Link>
        </div>
      ),
    },
    {
      key: "countdown",
      header: "Countdown",
      render: (value: any) => (
        <div className="font-inter-500-16">
          <Tooltip text="Time until the next auction">{value}d</Tooltip>
        </div>
      ),
    },
    {
      key: "category",
      header: "Category",
      render: (value: any) => (
        <div className="font-inter-500-16 line-18">{value}</div>
      ),
    },
    {
      key: "subcategory",
      header: "Subcategory",
      render: (value: any) => (
        <div className="font-inter-500-16 line-18">{value}</div>
      ),
    },
    {
      key: "fan1username",
      header: "Fan1",
      render: (value: any) => (
        <Link
          className="text-decoration-none font-inter-500-16 text-light-purple line-18"
          to={`/u/${value}`}
        >
          @{value}
        </Link>
      ),
    },
    {
      key: "annualized_value",
      header: "Value",
      render: (value: any) => (
        <div className="font-inter-500-16">
          <Tooltip text="Annualized value of the last price paid">
            {handlePrice(Number(value))}
          </Tooltip>
        </div>
      ),
    },
  ];

  const mobile_columns = [
    {
      key: "rank",
      header: "",
      className: "text-center position-relative",
      render: (value: any) => (
        <h4 className={"circle-rank-id me-4"}>{value}</h4>
      ),
    },
    {
      key: "entity",
      header: "",
      render: (value: any, row: any) => (
        <div className="d-flex align-items-center">
          <img src={row.image_url} className="image" alt={row.entity_name} />
          <div className="my-auto text-start line-21 ms-3 slabFigName">
            <Link
              to={`/${row.entity_public_id}`}
              state={{ id: row.entity_id }}
              className="text-decoration-none purple-color text-start font-18 font-500"
            >
              {row.entity_name}
            </Link>
            <div className="font-inter-300-15 text-extra-light-purple slabFanName">
              Fan1:{" "}
              <Link
                to={`/u/${row.fan1username}`}
                className="text-decoration-none text-extra-light-purple font-inter-300-15"
              >
                @{row.fan1username}
              </Link>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "countdown",
      header: "",
      render: (value: any) => (
        <div className="font-inter-400-15 pe-2">
          <Tooltip text="Time until the next auction">{value}d</Tooltip>
        </div>
      ),
    },
    {
      key: "annualized_value",
      header: "",
      render: (value: any) => (
        <div className="font-inter-400-15 me-1">
          <Tooltip text="Annualized value of the last price paid">
            {handlePrice(Number(value))}
          </Tooltip>
        </div>
      ),
    },
  ];

  const data =
    state.rankings &&
    state.rankings.map((item: any) => ({
      rank: item.rank,
      entity_name: item.entity_name,
      entity_id: item.entity_id,
      entity_public_id: item.entity_public_id,
      image_url: item.image_url,
      countdown: item.countdown,
      category: item.category,
      subcategory: item.subcategory,
      fan1username: item.fan1username,
      annualized_value: item.annualized_value,
    }));

  return (
    <>
      {state.isLoading ? (
        <div className="loader">
          <div className="spinner"></div>
        </div>
      ) : (
        <></>
      )}
      <div className="container Ranking-Page ps-2">
        <div className="d-flex justify-content-between align-items-baseline">
          <div className="font-500-44 pt-4 title">Rankings</div>
          <div className="d-lg-flex d-none justify-content-end pt-2 ms-2">
            <div className="py-4 rank-link">
              <Link
                to="/available"
                className="text-decoration-none toRight navtorank"
              >
                See Available Memberships
                <i className="fa-solid fa-arrow-right"></i>
              </Link>
            </div>
          </div>
        </div>
        <div className="Ranking-table">
          <div className="mt-2 py-3 d-flex pe-2 align-items-center">
            <h4 className="mt-2 mb-0 font-inter-500-16 font-20">Filter:</h4>
            <div className="filter-box mx-4 my-1 d-flex">
              <Button
                variant="dark"
                className={`py-1 px-4 first ${
                  filter.main === "all" && "box-active"
                }`}
                label="All"
                onClick={() => handleMainFilter("all")}
              />
              {state.categories &&
                state.categories.map((value) => (
                  <Button
                    variant="dark"
                    className={`py-1 px-4 middle ${
                      filter.main === value.name && "box-active"
                    }`}
                    key={value.id}
                    label={<>{value.name}</>}
                    onClick={() => handleMainFilter(value.name, value.id)}
                  />
                ))}
              <Dropdown
                icon={
                  <Button
                    variant="dark"
                    className={`py-1 px-4 last ${
                      filter.sub === "subcategory" && "box-active"
                    }`}
                    label="Subcategory"
                    withDropdownArrow={true}
                    isOpen={state.isOpen}
                  />
                }
              >
                <div style={{ width: "150px" }}>
                  {state.subcategories &&
                    state.subcategories.map((subcategory) => (
                      <div key={subcategory.id}>
                        {state.selectedSubcategories.includes(
                          subcategory.id
                        ) ? (
                          <p className="selected-category">
                            {subcategory.name}
                            <i
                              className="fa-solid fa-xmark"
                              onClick={() =>
                                handleRemoveSubcategory(subcategory.id)
                              }
                            />
                          </p>
                        ) : (
                          <p
                            onClick={() =>
                              handleSubcategorySelection(subcategory.id)
                            }
                          >
                            {subcategory.name}
                          </p>
                        )}
                      </div>
                    ))}
                </div>
              </Dropdown>
            </div>
          </div>

          <div className="ps-2 shadow-box vertical-scroll px-2">
            <Table
              columns={columns}
              data={data}
              className="ranking-table"
              headClassName="head"
              rowClassName="ranking-table-data"
              cellClassName="ranking-td"
            />
          </div>
        </div>

        <div className="ranking-tile-view">
          <div className="mt-2 pe-2 align-items-center">
            <div className="mobile-background-ellipse"></div>
            <div className="d-flex justify-content-between align-items-baseline">
              <div className="head-part ps-2" onClick={handleToggle}>
                Filter
                <i
                  className={`fa-solid fa-angle-down my-auto ms-2`}
                  style={{
                    transform: `rotate(${!state.isFilterOpen ? 0 : 180}deg)`,
                    transition: "transform 0.7s ease",
                  }}
                ></i>
              </div>
              <div className="d-lg-none d-flex justify-content-end pt-2 ms-2">
                <div className="rank-link">
                  <Link
                    to="/available"
                    className="text-decoration-none toRight navtorank"
                  >
                    See Available Memberships
                    <i className="fa-solid fa-arrow-right"></i>
                  </Link>
                </div>
              </div>
            </div>

            <div
              className={`${state.isFilterOpen ? "filter-div mt-2" : "d-none"}`}
            >
              <div className="d-inline-flex flex-wrap gap-2">
                <Button
                  variant="dark"
                  className={`circle-border ${
                    filter.main === "all" && "box-active"
                  }`}
                  label="All"
                  onClick={() => handleMainFilter("all")}
                />
                {state.categories &&
                  state.categories.map((value) => (
                    <Button
                      variant="dark"
                      className={`circle-border ${
                        filter.main === value.name && "box-active"
                      }`}
                      key={value.id}
                      label={value.name}
                      onClick={() => handleMainFilter(value.name, value.id)}
                    />
                  ))}
                <Dropdown
                  icon={
                    <Button
                      variant="dark"
                      className={`subcategory-border ${
                        filter.sub === "subcategory" && "box-active"
                      }`}
                      label="Subcategory"
                      withDropdownArrow={true}
                      isOpen={state.isOpen}
                    />
                  }
                >
                  <div style={{ width: "150px" }}>
                    {state.subcategories &&
                      state.subcategories.map((subcategory) => (
                        <div key={subcategory.id}>
                          {state.selectedSubcategories.includes(
                            subcategory.id
                          ) ? (
                            <p className="selected-category">
                              {subcategory.name}
                              <i
                                className="fa-solid fa-xmark"
                                onClick={() =>
                                  handleRemoveSubcategory(subcategory.id)
                                }
                              />
                            </p>
                          ) : (
                            <p
                              onClick={() =>
                                handleSubcategorySelection(subcategory.id)
                              }
                            >
                              {subcategory.name}
                            </p>
                          )}
                        </div>
                      ))}
                  </div>
                </Dropdown>
              </div>
            </div>
          </div>

          <div className="ranking-content shadow-box pe-0">
            <Table
              columns={mobile_columns}
              data={data}
              className="ranking-table"
              headClassName="head"
              rowClassName="ranking-table-data"
              cellClassName="ranking-td"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default RankingPage;
